import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "@/stores/hook";
import { synthesiseSpeech, transcribeVoiceClip } from "../api/ai.api";
import { useAIChat } from "../hooks/useAIChat";
import { VoiceContext, VoiceContextValue, VoicePhase } from "../hooks/voiceContext";

// ---------------------------------------------------------------------------
// VoiceProvider — the "talking" layer over Xale AI.
//
//   mic → (MediaRecorder clip) → /ai/voice/transcribe → text
//       → normal /ai/chat agent loop (tools, RBAC, tenant scope untouched)
//       → /ai/voice/speak → <audio> playback
//
// Two ways to use it:
//   • Push-to-talk: tap the mic, speak, tap again (or just stop talking —
//     ~1.2s of silence ends the clip).
//   • Hands-free: turn on "Voice mode" and it keeps the loop going — listen,
//     answer aloud, listen again — until you turn it off or say nothing.
//
// "Read aloud" makes TYPED questions speak their answers too. Voice-initiated
// turns always speak.
//
// Shared through context so the header toggles and the composer mic drive one
// state machine. No Redux — none of this needs to survive a panel remount.
// ---------------------------------------------------------------------------

const READ_ALOUD_KEY = "xale_ai_read_aloud";

/** Silence this long after speech ends → clip is done. */
const SILENCE_MS = 1200;
/** Hands-free: nothing said at all for this long → stop, don't loop forever. */
const NO_SPEECH_MS = 8000;
/** Hard ceiling per clip; a question is never this long. */
const MAX_CLIP_MS = 45000;
/** RMS above this counts as speech (0..1 scale). */
const SPEECH_THRESHOLD = 0.02;

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  return candidates.find((m) => MediaRecorder.isTypeSupported(m));
}

function isCancel(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === "CanceledError";
}

function errorMessage(err: unknown): string | undefined {
  const e = err as { response?: { data?: { message?: string } }; message?: string } | null;
  return e?.response?.data?.message || e?.message;
}

function readStoredBool(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeStoredBool(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* private mode etc. — ignore */
  }
}

export function VoiceProvider({ children }: { children: ReactNode }) {
  const { send, cancel: cancelChat } = useAIChat();
  const isOpen = useAppSelector((s) => s.aiChat.isOpen);
  const hasModel = useAppSelector((s) => s.aiChat.currentModelId !== null);

  const [phase, setPhase] = useState<VoicePhase>("idle");
  const [handsFree, setHandsFree] = useState(false);
  const [readAloud, setReadAloud] = useState(() => readStoredBool(READ_ALOUD_KEY));
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  // Latest values for use inside async chains without stale closures.
  const sendRef = useRef(send);
  sendRef.current = send;
  const cancelChatRef = useRef(cancelChat);
  cancelChatRef.current = cancelChat;
  /**
   * Turn token. Every user action that starts something new (mic tap, typed
   * send, Stop) bumps it; async chains compare against the value they
   * captured and bail out if the world moved on — so an interrupted answer
   * can never resume a stale hands-free loop.
   */
  const turnRef = useRef(0);
  const handsFreeRef = useRef(handsFree);
  handsFreeRef.current = handsFree;
  const readAloudRef = useRef(readAloud);
  readAloudRef.current = readAloud;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const hasModelRef = useRef(hasModel);
  hasModelRef.current = hasModel;

  // Live recording resources.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const meterRafRef = useRef<number | null>(null);
  const clipTimersRef = useRef<number[]>([]);
  /** true when the recorder was stopped to DISCARD the clip (not transcribe). */
  const discardClipRef = useRef(false);

  // Network + playback in flight.
  const abortRef = useRef<AbortController | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  /** Resolves the pending playBlob promise when playback is cut short. */
  const playbackDoneRef = useRef<(() => void) | null>(null);

  const clearClipTimers = () => {
    clipTimersRef.current.forEach((t) => window.clearTimeout(t));
    clipTimersRef.current = [];
  };

  const releaseMic = useCallback(() => {
    clearClipTimers();
    if (meterRafRef.current !== null) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setLevel(0);
  }, []);

  const stopPlayback = useCallback(() => {
    const el = audioElRef.current;
    if (el) {
      el.onended = null;
      el.onerror = null;
      el.pause();
      el.src = "";
      audioElRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
    const done = playbackDoneRef.current;
    playbackDoneRef.current = null;
    done?.();
  }, []);

  /** Play a TTS blob; resolves when playback finishes or fails. */
  const playBlob = useCallback(
    (blob: Blob) =>
      new Promise<void>((resolve) => {
        stopPlayback();
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        const el = new Audio(url);
        audioElRef.current = el;
        const finish = () => {
          if (playbackDoneRef.current === finish) playbackDoneRef.current = null;
          resolve();
        };
        playbackDoneRef.current = finish;
        el.onended = finish;
        el.onerror = finish;
        el.play().catch(finish);
      }),
    [stopPlayback]
  );

  /** Browser voice as a last resort when the server can't synthesise. */
  const speakWithBrowser = useCallback(
    (text: string) =>
      new Promise<void>((resolve) => {
        if (typeof speechSynthesis === "undefined") return resolve();
        const plain = text.replace(/[*_`#>|]/g, " ").slice(0, 1500);
        const utt = new SpeechSynthesisUtterance(plain);
        utt.onend = () => resolve();
        utt.onerror = () => resolve();
        speechSynthesis.cancel();
        speechSynthesis.speak(utt);
      }),
    []
  );

  const speak = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      setPhase("speaking");
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const blob = await synthesiseSpeech(text, controller.signal);
        if (controller.signal.aborted) return;
        await playBlob(blob);
      } catch (err) {
        if (controller.signal.aborted || isCancel(err)) return;
        // Server TTS unavailable (no Gemini/OpenAI key, quota…) — still talk.
        await speakWithBrowser(text);
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [playBlob, speakWithBrowser]
  );

  // Forward declaration so the loop can re-enter listening after speaking.
  const startListeningRef = useRef<() => void>(() => {});

  /** Route a transcript (or typed text) through chat, then speak if wanted. */
  const runTurn = useCallback(
    async (text: string, spokenInput: boolean) => {
      const myTurn = turnRef.current;
      setPhase("thinking");
      const result = await sendRef.current(text);
      if (turnRef.current !== myTurn) return; // user moved on mid-answer
      if (!result.ok) {
        setPhase("idle");
        setHandsFree(false);
        return;
      }
      if (spokenInput || readAloudRef.current) {
        await speak(result.text);
        if (turnRef.current !== myTurn) return; // cut off by Stop / new mic tap
      }
      if (handsFreeRef.current && spokenInput) {
        startListeningRef.current();
      } else {
        setPhase("idle");
      }
    },
    [speak]
  );

  const handleClip = useCallback(
    async (blob: Blob) => {
      if (!blob.size) {
        setPhase("idle");
        return;
      }
      const myTurn = turnRef.current;
      setPhase("transcribing");
      const controller = new AbortController();
      abortRef.current = controller;
      let text = "";
      try {
        text = await transcribeVoiceClip(blob, controller.signal);
        if (turnRef.current !== myTurn) return;
      } catch (err) {
        if (controller.signal.aborted || isCancel(err) || turnRef.current !== myTurn) return;
        const msg = errorMessage(err) || "Couldn't understand the recording";
        setError(msg);
        setPhase("idle");
        setHandsFree(false);
        return;
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
      if (!text) {
        setError("Didn't catch that — try again.");
        setPhase("idle");
        return;
      }
      await runTurn(text, true);
    },
    [runTurn]
  );

  const startListening = useCallback(async () => {
    if (!supported) {
      setError("Voice needs a browser with microphone support.");
      return;
    }
    if (!hasModelRef.current) {
      setError("Pick a model first.");
      return;
    }
    if (phaseRef.current !== "idle" && phaseRef.current !== "speaking") return;
    turnRef.current++;
    stopPlayback();
    abortRef.current?.abort();
    abortRef.current = null;
    setError(null);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch {
      setError("Microphone access was blocked. Allow the mic in your browser and try again.");
      setPhase("idle");
      setHandsFree(false);
      return;
    }
    streamRef.current = stream;

    const mime = pickRecorderMime();
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    recorderRef.current = recorder;
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const discard = discardClipRef.current;
      discardClipRef.current = false;
      const blob = new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" });
      releaseMic();
      if (discard) {
        setPhase("idle");
        return;
      }
      void handleClip(blob);
    };
    discardClipRef.current = false;
    recorder.start(250);
    setPhase("listening");

    // Level meter + end-of-speech detection.
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      let heardSpeech = false;
      let silenceSince = 0;
      const startedAt = performance.now();

      const tick = () => {
        if (recorderRef.current !== recorder || recorder.state !== "recording") return;
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        // Quantise so the meter re-renders a few times a second, not 60fps.
        const lvl = Math.round(Math.min(1, rms * 8) * 10) / 10;
        setLevel((prev) => (prev === lvl ? prev : lvl));
        const now = performance.now();
        if (rms > SPEECH_THRESHOLD) {
          heardSpeech = true;
          silenceSince = 0;
        } else if (heardSpeech) {
          if (!silenceSince) silenceSince = now;
          if (now - silenceSince > SILENCE_MS) {
            recorder.stop();
            return;
          }
        } else if (now - startedAt > NO_SPEECH_MS) {
          // Nobody's talking — release the mic instead of looping forever.
          discardClipRef.current = true;
          setHandsFree(false);
          setError("Didn't hear anything.");
          recorder.stop();
          return;
        }
        meterRafRef.current = requestAnimationFrame(tick);
      };
      meterRafRef.current = requestAnimationFrame(tick);
    }

    clipTimersRef.current.push(
      window.setTimeout(() => {
        if (recorderRef.current === recorder && recorder.state === "recording") recorder.stop();
      }, MAX_CLIP_MS)
    );
  }, [supported, stopPlayback, releaseMic, handleClip]);
  startListeningRef.current = () => void startListening();

  const stopListening = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state === "recording") r.stop();
  }, []);

  const stopAll = useCallback(() => {
    turnRef.current++;
    abortRef.current?.abort();
    abortRef.current = null;
    cancelChatRef.current();
    stopPlayback();
    const r = recorderRef.current;
    if (r && r.state === "recording") {
      discardClipRef.current = true;
      r.stop();
    } else {
      releaseMic();
    }
    setHandsFree(false);
    setPhase("idle");
  }, [stopPlayback, releaseMic]);

  const toggleHandsFree = useCallback(() => {
    if (handsFreeRef.current) {
      stopAll();
      return;
    }
    setHandsFree(true);
    void startListening();
  }, [stopAll, startListening]);

  const toggleReadAloud = useCallback(() => {
    setReadAloud((v) => {
      writeStoredBool(READ_ALOUD_KEY, !v);
      if (v) stopPlayback();
      return !v;
    });
  }, [stopPlayback]);

  const sendText = useCallback(
    async (text: string) => {
      if (phaseRef.current !== "idle") stopAll();
      turnRef.current++;
      setError(null);
      await runTurn(text, false);
    },
    [runTurn, stopAll]
  );

  // Closing the panel or unmounting ends everything (mic light off).
  useEffect(() => {
    if (!isOpen && phaseRef.current !== "idle") stopAll();
  }, [isOpen, stopAll]);
  useEffect(() => () => stopAll(), [stopAll]);

  const value = useMemo<VoiceContextValue>(
    () => ({
      phase,
      handsFree,
      readAloud,
      level,
      error,
      supported,
      startListening: () => void startListening(),
      stopListening,
      toggleHandsFree,
      toggleReadAloud,
      sendText,
      stopAll,
    }),
    [
      phase,
      handsFree,
      readAloud,
      level,
      error,
      supported,
      startListening,
      stopListening,
      toggleHandsFree,
      toggleReadAloud,
      sendText,
      stopAll,
    ]
  );

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}
